// SNIGJ - Cases Page

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { casesAPI, referenceAPI } from '../lib/api';
import api from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import PersonSearchCombo from '../components/shared/PersonSearchCombo';
import FileUploader from '../components/shared/FileUploader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Search, 
  Filter,
  Eye,
  Edit,
  FileText,
  Calendar,
  MapPin,
  Shield,
  Lock,
  Upload,
  User,
  Loader2,
  Hash
} from 'lucide-react';
import { toast } from 'sonner';

const CasesPage = () => {
  const { language, user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [filters, setFilters] = useState({
    case_type: '',
    status: '',
    classification: ''
  });
  const [formData, setFormData] = useState({
    case_number: '', // Will be auto-generated
    case_type: 'CRIMINAL',
    title: '',
    description: '',
    classification: 'PUBLIC',
    status: 'OPEN',
    jurisdiction: user?.jurisdiction || 'KINSHASA',
    territorial_level: 'CITY',
    charges: [],
    plaintiff_id: '',
    plaintiff_name: '',
    defendant_id: '',
    defendant_name: ''
  });

  useEffect(() => {
    loadCases();
  }, [filters, search]);

  const loadCases = async () => {
    try {
      setLoading(true);
      const params = { ...filters, search, limit: 100 };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      const response = await casesAPI.list(params);
      setCases(response.data);
    } catch (error) {
      console.error('Failed to load cases:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  // Handle plaintiff selection
  const handlePlaintiffSelect = (person) => {
    if (person) {
      setFormData(prev => ({
        ...prev,
        plaintiff_id: person.user_id || person.id,
        plaintiff_name: person.name || `${person.first_name} ${person.last_name}`
      }));
    } else {
      setFormData(prev => ({ ...prev, plaintiff_id: '', plaintiff_name: '' }));
    }
  };

  // Handle defendant selection
  const handleDefendantSelect = (person) => {
    if (person) {
      setFormData(prev => ({
        ...prev,
        defendant_id: person.user_id || person.id,
        defendant_name: person.name || `${person.first_name} ${person.last_name}`
      }));
    } else {
      setFormData(prev => ({ ...prev, defendant_id: '', defendant_name: '' }));
    }
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      // Include attachments in case data
      const caseData = {
        ...formData,
        attachments: attachments.map(f => f.file_id),
        // Remove case_number if empty - backend will auto-generate
        case_number: formData.case_number || undefined
      };
      await casesAPI.create(caseData);
      toast.success(language === 'fr' ? 'Dossier créé' : 'Case created');
      setShowCreateModal(false);
      loadCases();
      // Reset form
      setFormData({
        case_number: '',
        case_type: 'CRIMINAL',
        title: '',
        description: '',
        classification: 'PUBLIC',
        status: 'OPEN',
        jurisdiction: user?.jurisdiction || 'KINSHASA',
        territorial_level: 'CITY',
        charges: [],
        plaintiff_id: '',
        plaintiff_name: '',
        defendant_id: '',
        defendant_name: ''
      });
      setAttachments([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de création' : 'Failed to create case'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      OPEN: 'status-open',
      IN_PROGRESS: 'status-pending',
      PENDING: 'status-pending',
      CLOSED: 'status-closed',
      ARCHIVED: 'status-closed'
    };
    return `px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-gray-100'}`;
  };

  const getClassificationBadge = (classification) => {
    return `px-2 py-1 rounded text-xs font-medium uppercase tracking-wider classification-${classification?.toLowerCase()}`;
  };

  const caseTypes = ['CRIMINAL', 'CIVIL', 'ADMINISTRATIVE'];
  const statuses = ['OPEN', 'IN_PROGRESS', 'PENDING', 'CLOSED', 'ARCHIVED'];
  const classifications = ['PUBLIC', 'RESTRICTED', 'CONFIDENTIAL', 'SECRET', 'TOP_SECRET'];

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6" data-testid="cases-page">
        {/* Header - Mobile Optimized */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight">
              {t(language, 'cases.title')}
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">
              {language === 'fr' ? 'Gérez les dossiers judiciaires' : 'Manage judicial cases'}
            </p>
          </div>
          <div className="flex justify-end">
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
              <DialogTrigger asChild>
                <Button className="gap-2" size="sm" data-testid="new-case-btn">
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">{t(language, 'cases.newCase')}</span>
                  <span className="sm:hidden">{language === 'fr' ? 'Nouveau' : 'New'}</span>
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">{t(language, 'cases.newCase')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCase} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Hash className="w-4 h-4" />
                      {t(language, 'cases.caseNumber')}
                    </Label>
                    <Input
                      value={formData.case_number}
                      onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
                      placeholder={language === 'fr' ? "Auto-généré si vide" : "Auto-generated if empty"}
                      data-testid="case-number-input"
                      className="font-mono"
                    />
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' ? 'Laissez vide pour numérotation automatique' : 'Leave empty for auto-numbering'}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>{t(language, 'cases.caseType')}</Label>
                    <Select
                      value={formData.case_type}
                      onValueChange={(value) => setFormData({ ...formData, case_type: value })}
                    >
                      <SelectTrigger data-testid="case-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {caseTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {t(language, `cases.${type.toLowerCase()}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={language === 'fr' ? "Titre du dossier" : "Case title"}
                    required
                    data-testid="case-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t(language, 'cases.description')}</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={language === 'fr' ? "Description du dossier" : "Case description"}
                    rows={3}
                    data-testid="case-description-input"
                  />
                </div>

                {/* Parties Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {language === 'fr' ? 'Parties au dossier' : 'Case parties'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <PersonSearchCombo
                        label={language === 'fr' ? 'Plaignant / Demandeur' : 'Plaintiff'}
                        placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                        onPersonSelect={handlePlaintiffSelect}
                        showCreateOption={true}
                      />
                    </div>
                    <div>
                      <PersonSearchCombo
                        label={language === 'fr' ? 'Prévenu / Défendeur' : 'Defendant'}
                        placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                        onPersonSelect={handleDefendantSelect}
                        showCreateOption={true}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t(language, 'cases.classification')}</Label>
                    <Select
                      value={formData.classification}
                      onValueChange={(value) => setFormData({ ...formData, classification: value })}
                    >
                      <SelectTrigger data-testid="case-classification-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {classifications.map((c) => (
                          <SelectItem key={c} value={c}>
                            {t(language, `classifications.${c}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t(language, 'cases.status')}</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="case-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statuses.map((s) => (
                          <SelectItem key={s} value={s}>
                            {t(language, `cases.${s.toLowerCase().replace('_', '')}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t(language, 'cases.jurisdiction')}</Label>
                    <Input
                      value={formData.jurisdiction}
                      onChange={(e) => setFormData({ ...formData, jurisdiction: e.target.value })}
                      placeholder="KINSHASA"
                      required
                      data-testid="case-jurisdiction-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Niveau territorial' : 'Territorial Level'}</Label>
                    <Select
                      value={formData.territorial_level}
                      onValueChange={(value) => setFormData({ ...formData, territorial_level: value })}
                    >
                      <SelectTrigger data-testid="case-territorial-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SUB_COMMISSION">Sous-commission</SelectItem>
                        <SelectItem value="COMMISSION">Commission</SelectItem>
                        <SelectItem value="TERRITORY">Territoire</SelectItem>
                        <SelectItem value="CITY">Ville</SelectItem>
                        <SelectItem value="PROVINCE">Province</SelectItem>
                        <SelectItem value="NATIONAL">National</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* File Attachments Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    {language === 'fr' ? 'Pièces jointes' : 'Attachments'}
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    {language === 'fr' 
                      ? 'Documents, photos, audio ou vidéo liés au dossier'
                      : 'Documents, photos, audio or video related to the case'}
                  </p>
                  <FileUploader
                    value={attachments}
                    onChange={setAttachments}
                    entityType="case"
                    maxFiles={20}
                    maxFileSize={100 * 1024 * 1024}
                    acceptedTypes="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                    {t(language, 'common.cancel')}
                  </Button>
                  <Button type="submit" disabled={isSubmitting} data-testid="create-case-submit">
                    {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {t(language, 'common.save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filters - Mobile Optimized */}
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 items-stretch sm:items-end">
              <div className="w-full sm:flex-1 sm:min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t(language, 'common.search') + '...'}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="cases-search"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-4">
                <div className="w-full sm:w-[150px]">
                  <Select
                    value={filters.case_type || "ALL"}
                    onValueChange={(value) => setFilters({ ...filters, case_type: value === "ALL" ? "" : value })}
                  >
                    <SelectTrigger data-testid="filter-case-type">
                      <SelectValue placeholder={t(language, 'cases.caseType')} />
                    </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                    {caseTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(language, `cases.${type.toLowerCase()}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </div>
                <div className="w-full sm:w-[150px]">
                  <Select
                    value={filters.status || "ALL"}
                    onValueChange={(value) => setFilters({ ...filters, status: value === "ALL" ? "" : value })}
                  >
                    <SelectTrigger data-testid="filter-status">
                      <SelectValue placeholder={t(language, 'cases.status')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                      {statuses.map((s) => (
                        <SelectItem key={s} value={s}>
                          {t(language, `cases.${s.toLowerCase().replace('_', '')}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button variant="outline" onClick={loadCases} className="gap-2 w-full sm:w-auto">
                <Filter className="w-4 h-4" />
                {t(language, 'common.filter')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cases Table - Desktop */}
        <Card className="hidden md:block">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr className="bg-muted/50">
                    <th>{t(language, 'cases.caseNumber')}</th>
                    <th>{t(language, 'cases.caseType')}</th>
                    <th>{language === 'fr' ? 'Titre' : 'Title'}</th>
                    <th>{t(language, 'cases.status')}</th>
                    <th>{t(language, 'cases.classification')}</th>
                    <th>{t(language, 'cases.jurisdiction')}</th>
                    <th>{t(language, 'cases.createdAt')}</th>
                    <th>{t(language, 'cases.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <td key={j}><div className="h-4 skeleton" /></td>
                        ))}
                      </tr>
                    ))
                  ) : cases.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-muted-foreground">
                        {t(language, 'common.noData')}
                      </td>
                    </tr>
                  ) : (
                    cases.map((caseItem) => (
                      <tr key={caseItem.case_id} data-testid={`case-row-${caseItem.case_id}`}>
                        <td>
                          <span className="font-mono text-sm flex items-center gap-1">
                            {caseItem.case_number}
                            {['SECRET', 'TOP_SECRET'].includes(caseItem.classification) && (
                              <Lock className="w-3 h-3 text-red-500" title={language === 'fr' ? 'Accès restreint' : 'Restricted access'} />
                            )}
                          </span>
                        </td>
                        <td>
                          <span className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-muted-foreground" />
                            {t(language, `cases.${caseItem.case_type.toLowerCase()}`)}
                          </span>
                        </td>
                        <td className="max-w-[200px] truncate">{caseItem.title}</td>
                        <td>
                          <span className={getStatusBadge(caseItem.status)}>
                            {t(language, `cases.${caseItem.status.toLowerCase().replace('_', '')}`)}
                          </span>
                        </td>
                        <td>
                          <span className={getClassificationBadge(caseItem.classification)}>
                            {t(language, `classifications.${caseItem.classification}`)}
                          </span>
                        </td>
                        <td>
                          <span className="flex items-center gap-1 text-sm">
                            <MapPin className="w-3 h-3" />
                            {caseItem.jurisdiction}
                          </span>
                        </td>
                        <td>
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            {new Date(caseItem.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => navigate(`/cases/${caseItem.case_id}`)}
                              data-testid={`view-case-${caseItem.case_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => navigate(`/cases/${caseItem.case_id}/edit`)}
                              data-testid={`edit-case-${caseItem.case_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Cases Cards - Mobile */}
        <div className="md:hidden space-y-3">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="h-4 skeleton w-1/2" />
                    <div className="h-3 skeleton w-full" />
                    <div className="h-3 skeleton w-2/3" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : cases.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                {t(language, 'common.noData')}
              </CardContent>
            </Card>
          ) : (
            cases.map((caseItem) => (
              <Card key={caseItem.case_id} className="overflow-hidden" data-testid={`case-card-${caseItem.case_id}`}>
                <CardContent className="p-0">
                  <div className="p-4 space-y-3">
                    {/* Header with case number and status */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-sm font-medium flex items-center gap-1">
                          {caseItem.case_number}
                          {['SECRET', 'TOP_SECRET'].includes(caseItem.classification) && (
                            <Lock className="w-3 h-3 text-red-500" />
                          )}
                        </p>
                        <h3 className="font-medium text-base mt-1 line-clamp-2">{caseItem.title}</h3>
                      </div>
                      <span className={getStatusBadge(caseItem.status) + " text-xs whitespace-nowrap"}>
                        {t(language, `cases.${caseItem.status.toLowerCase().replace('_', '')}`)}
                      </span>
                    </div>
                    
                    {/* Info grid */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{t(language, `cases.${caseItem.case_type.toLowerCase()}`)}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{caseItem.jurisdiction}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Shield className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className={`text-xs px-1.5 py-0.5 rounded classification-${caseItem.classification?.toLowerCase()}`}>
                          {t(language, `classifications.${caseItem.classification}`)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{new Date(caseItem.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions footer */}
                  <div className="flex border-t divide-x">
                    <button 
                      onClick={() => navigate(`/cases/${caseItem.case_id}`)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium hover:bg-muted/50 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      {language === 'fr' ? 'Voir' : 'View'}
                    </button>
                    <button 
                      onClick={() => navigate(`/cases/${caseItem.case_id}/edit`)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium hover:bg-muted/50 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      {language === 'fr' ? 'Modifier' : 'Edit'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CasesPage;
